/**
 * Usuarios y accesos.
 *
 * Dar de alta a alguien son dos cosas distintas que aquí ocurren juntas:
 * la **cuenta** (correo y contraseña, en Authentication) y el **perfil**
 * (el rol, en Firestore). Sin perfil la cuenta entra pero no puede nada.
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Check, KeyRound, Loader2, RefreshCw, ShieldOff, UserPlus, Users } from 'lucide-react';
import { Boton, Campo, Chip, Selector, Tarjeta, TarjetaTitulo, Vacio } from '@/components/ui';
import { useDialogo } from '@/components/ui/dialogo';
import { useAuth, NOMBRE_ROL, type Perfil, type Rol } from '@/hooks/useAuth';
import { useCatalogos } from '@/hooks/useCatalogos';
import { modoLocal } from '@/db/repositorio';
import {
  cambiarActivo,
  cambiarRol,
  claveSugerida,
  crearUsuario,
  enviarRestablecer,
  listarUsuarios,
} from '@/db/altaUsuarios';

/** Qué hace cada rol, en una línea. Para no tener que adivinar al elegir. */
const QUE_HACE: Record<Rol, string> = {
  ADMIN: 'Todo, incluidos usuarios y guías ya facturadas.',
  JEFE_CONTRATO: 'Estados de pago, contrato y catálogos. No administra usuarios.',
  JEFE_PLANTA: 'Guías de su planta, anulaciones y jornada de su gente.',
  OPERADOR_PLANTA: 'Carga guías y marca su propia jornada. No anula ni corrige.',
  CALIDAD: 'Consulta y catálogos. No carga guías.',
  RRHH: 'Jornada, horas extras y trabajadores. No ve guías.',
  LECTOR: 'Sólo mira: resumen y reportes.',
};

const ROLES = Object.keys(NOMBRE_ROL) as Rol[];

export default function Usuarios() {
  const { perfil: yo } = useAuth();
  const { catalogos } = useCatalogos();
  const { confirmar } = useDialogo();

  const [lista, setLista] = useState<Perfil[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const [abriendo, setAbriendo] = useState(false);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [clave, setClave] = useState('');
  const [rol, setRol] = useState<Rol>('OPERADOR_PLANTA');
  const [plantaId, setPlantaId] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setLista(await listarUsuarios());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron leer los usuarios.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (modoLocal) {
      setCargando(false);
      return;
    }
    void cargar();
  }, [cargar]);

  function abrirFormulario() {
    setNombre('');
    setEmail('');
    setClave(claveSugerida());
    setRol('OPERADOR_PLANTA');
    setPlantaId('');
    setAviso(null);
    setError(null);
    setAbriendo(true);
  }

  async function darDeAlta(e: FormEvent) {
    e.preventDefault();
    setTrabajando(true);
    setError(null);
    setAviso(null);
    try {
      const p = await crearUsuario({
        nombre,
        email,
        clave,
        rol,
        plantaId: plantaId || null,
      });
      setAviso(
        `${p.nombre} ya puede entrar con ${p.email} y la contraseña ${clave}. ` +
          'Dísela por un medio seguro y pídele que la cambie al primer ingreso.',
      );
      setAbriendo(false);
      await cargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el usuario.');
    } finally {
      setTrabajando(false);
    }
  }

  async function editarRol(u: Perfil, nuevo: Rol) {
    setError(null);
    setAviso(null);
    try {
      await cambiarRol(u.uid, nuevo, u.plantaId);
      setLista((xs) => xs.map((x) => (x.uid === u.uid ? { ...x, rol: nuevo } : x)));
      setAviso(`${u.nombre || u.email} ahora es ${NOMBRE_ROL[nuevo]}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar el rol.');
    }
  }

  async function editarPlanta(u: Perfil, nueva: string) {
    setError(null);
    try {
      await cambiarRol(u.uid, u.rol, nueva || null);
      setLista((xs) => xs.map((x) => (x.uid === u.uid ? { ...x, plantaId: nueva || null } : x)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar la planta.');
    }
  }

  async function alternarAcceso(u: Perfil) {
    const quitando = u.activo;
    if (quitando) {
      const si = await confirmar({
        titulo: `Quitar el acceso a ${u.nombre || u.email}`,
        mensaje:
          'La cuenta sigue existiendo, pero deja de poder entrar y de ver datos. ' +
          'Se puede volver a activar cuando quieras. Lo que ya cargó no se toca.',
        aceptar: 'Quitar acceso',
        tono: 'peligro',
      });
      if (!si) return;
    }
    setError(null);
    try {
      await cambiarActivo(u.uid, !u.activo);
      setLista((xs) => xs.map((x) => (x.uid === u.uid ? { ...x, activo: !u.activo } : x)));
      setAviso(
        quitando
          ? `${u.nombre || u.email} ya no puede entrar.`
          : `${u.nombre || u.email} vuelve a tener acceso.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar el acceso.');
    }
  }

  async function restablecer(u: Perfil) {
    setError(null);
    try {
      await enviarRestablecer(u.email);
      setAviso(`Se envió un correo a ${u.email} para que elija una contraseña nueva.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar el correo.');
    }
  }

  /** Cuántos administradores activos quedan: no hay que quedarse sin ninguno. */
  const adminsActivos = useMemo(
    () => lista.filter((u) => u.rol === 'ADMIN' && u.activo).length,
    [lista],
  );

  if (modoLocal) {
    return (
      <Tarjeta>
        <TarjetaTitulo>Usuarios y accesos</TarjetaTitulo>
        <div className="p-4 text-xs text-texto-mute">
          Sin Firebase conectado no hay usuarios que administrar: la app corre abierta en este
          dispositivo.
        </div>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta>
      <TarjetaTitulo
        accion={
          <div className="flex items-center gap-2">
            <Chip nivel="NEUTRO" conPunto={false}>
              <Users size={12} />
              {lista.length}
            </Chip>
            <Boton
              variante="secundario"
              tamano="sm"
              icono={<RefreshCw size={14} />}
              onClick={() => void cargar()}
            >
              Recargar
            </Boton>
            <Boton
              variante="primario"
              tamano="sm"
              icono={<UserPlus size={14} />}
              onClick={abrirFormulario}
            >
              Nuevo usuario
            </Boton>
          </div>
        }
      >
        Usuarios y accesos
      </TarjetaTitulo>

      <div className="space-y-3 p-4">
        {error && (
          <p className="rounded-control border border-fluor-rojo/40 bg-fluor-rojo/10 px-3 py-2 text-xs text-fluor-rojo">
            {error}
          </p>
        )}
        {aviso && (
          <p className="rounded-control border border-fluor-verde/40 bg-fluor-verde/10 px-3 py-2 text-xs text-fluor-verde">
            {aviso}
          </p>
        )}

        {abriendo && (
          <form
            onSubmit={(e) => void darDeAlta(e)}
            className="space-y-3 rounded-tarjeta border border-linea bg-petroleo-800/40 p-4"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo
                etiqueta="Nombre"
                name="alta-nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Nombre y apellido"
                required
                autoComplete="off"
              />
              <Campo
                etiqueta="Correo"
                name="alta-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="persona@empresa.cl"
                required
                autoComplete="off"
              />
              <div>
                <Campo
                  etiqueta="Contraseña provisoria"
                  name="alta-clave"
                  value={clave}
                  onChange={(e) => setClave(e.target.value)}
                  minLength={6}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setClave(claveSugerida())}
                  className="mt-1 text-[11px] text-petroleo-300 underline-offset-2 hover:underline"
                >
                  Sugerir otra
                </button>
              </div>
              <Selector
                etiqueta="Rol"
                name="alta-rol"
                value={rol}
                onChange={(e) => setRol(e.target.value as Rol)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {NOMBRE_ROL[r]}
                  </option>
                ))}
              </Selector>
            </div>

            <p className="text-[11px] text-texto-mute">{QUE_HACE[rol]}</p>

            {(rol === 'JEFE_PLANTA' || rol === 'OPERADOR_PLANTA') && (
              <Selector
                etiqueta="Planta"
                name="alta-planta"
                value={plantaId}
                onChange={(e) => setPlantaId(e.target.value)}
              >
                <option value="">Todas</option>
                {catalogos.plantas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.codigo}
                  </option>
                ))}
              </Selector>
            )}

            <div className="flex gap-2">
              <Boton type="submit" variante="primario" disabled={trabajando}>
                {trabajando ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                Crear usuario
              </Boton>
              <Boton type="button" variante="secundario" onClick={() => setAbriendo(false)}>
                Cancelar
              </Boton>
            </div>
          </form>
        )}

        {cargando ? (
          <div className="flex items-center gap-2 p-4 text-xs text-texto-mute">
            <Loader2 size={14} className="animate-spin" />
            Leyendo usuarios…
          </div>
        ) : lista.length === 0 ? (
          <Vacio titulo="Sin usuarios" />
        ) : (
          <div className="space-y-2">
            {lista.map((u) => {
              const soyYo = u.uid === yo?.uid;
              const ultimoAdmin = u.rol === 'ADMIN' && u.activo && adminsActivos <= 1;
              return (
                <div
                  key={u.uid}
                  className="flex flex-wrap items-center gap-3 rounded-tarjeta border border-linea bg-petroleo-800/30 p-3"
                >
                  <div className="min-w-40 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {u.nombre || u.email}
                      {soyYo && (
                        <span className="rounded bg-petroleo-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-petroleo-300">
                          tú
                        </span>
                      )}
                    </div>
                    <div className="truncate text-[11px] text-texto-mute">{u.email}</div>
                  </div>

                  <Selector
                    name={`rol-${u.uid}`}
                    aria-label={`Rol de ${u.nombre || u.email}`}
                    value={u.rol}
                    disabled={soyYo}
                    onChange={(e) => void editarRol(u, e.target.value as Rol)}
                    className="w-48"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {NOMBRE_ROL[r]}
                      </option>
                    ))}
                  </Selector>

                  {(u.rol === 'JEFE_PLANTA' || u.rol === 'OPERADOR_PLANTA') && (
                    <Selector
                      name={`planta-${u.uid}`}
                      aria-label={`Planta de ${u.nombre || u.email}`}
                      value={u.plantaId ?? ''}
                      onChange={(e) => void editarPlanta(u, e.target.value)}
                      className="w-40"
                    >
                      <option value="">Todas</option>
                      {catalogos.plantas.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.codigo}
                        </option>
                      ))}
                    </Selector>
                  )}

                  <Chip nivel={u.activo ? 'VERDE' : 'ROJO'}>
                    {u.activo ? 'Con acceso' : 'Sin acceso'}
                  </Chip>

                  <div className="flex gap-1.5">
                    <Boton
                      variante="secundario"
                      tamano="sm"
                      icono={<KeyRound size={14} />}
                      onClick={() => void restablecer(u)}
                      title="Enviar correo para restablecer la contraseña"
                    >
                      Contraseña
                    </Boton>
                    <Boton
                      variante="secundario"
                      tamano="sm"
                      icono={<ShieldOff size={14} />}
                      disabled={soyYo || ultimoAdmin}
                      onClick={() => void alternarAcceso(u)}
                      title={
                        soyYo
                          ? 'No puedes quitarte el acceso a ti mismo'
                          : ultimoAdmin
                            ? 'Es el único administrador activo'
                            : undefined
                      }
                    >
                      {u.activo ? 'Quitar acceso' : 'Dar acceso'}
                    </Boton>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-[11px] leading-relaxed text-texto-mute">
          Quitar el acceso desactiva el perfil: la cuenta sigue existiendo pero no puede entrar ni
          leer nada. Borrar la cuenta de raíz se hace desde la consola de Firebase, en
          Authentication.
        </p>
      </div>
    </Tarjeta>
  );
}
